<template>
  <div class="tce-video-youtube">
    <tce-load-script
      v-if="loadYouTubeScript"
      :src="YOUTUBE_SCRIPT_URL"
      :name="YOUTUBE_SCRIPT_NAME"
      @loaded="setupPlayer"
    />

    <div v-show="!showPlaceholder" class="tce-video-youtube__video-wrapper">
      <div :id="playerId" class="tce-video-youtube__video"></div>
    </div>

    <button
      v-if="!showPlaceholder"
      class="tce-video-youtube__stop"
      :aria-label="stopButtonAriaLabel"
      @click="stop"
    >
      <img src="/ui/icons/close-icon.svg" alt="" />
    </button>

    <div v-if="showPlaceholder" class="tce-video-youtube__placeholder">
      <picture class="tce-video-youtube__picture">
        <source media="(min-width: 768px)" :srcset="image.lg" />
        <img class="tce-video-youtube__image" :src="image.sm" alt="" />
      </picture>

      <div class="tce-video-youtube__info">
        <div
          v-if="title"
          :class="[
            'tce-video-youtube__title',
            { 'tce-video-youtube__title--is-white': isTitleWhite },
          ]"
        >
          {{ title }}
        </div>

        <button
          ref="playButton"
          class="tce-video-youtube__play"
          :aria-label="playButtonAriaLabel"
          @click="play"
        >
          <img src="/ui/icons/play.svg" alt="" />
        </button>
      </div>
    </div>

    <p v-if="description" class="tce-video-youtube__description">{{ description }}</p>
  </div>
</template>

<script>
export default {
  name: 'tce-video-youtube',
};
</script>

<script setup>
import { ref } from 'vue';
import TceLoadScript from '../TceLoadScript/TceLoadScript.vue';

const props = defineProps({
  image: {
    required: true,
    type: Object,
    validator: (obj) => ['sm', 'lg'].every((prop) => prop in obj),
  },
  title: { type: String, default: '' },
  isTitleWhite: { type: Boolean, default: false },
  description: { type: String, default: '' },
  playButtonAriaLabel: { type: String, default: '' },
  stopButtonAriaLabel: { type: String, default: '' },

  videoId: { required: true, type: String },
  host: { type: String, default: 'https://www.youtube.com' },
});

const loadYouTubeScript = ref(false);
const YOUTUBE_SCRIPT_NAME = 'youtube';
const YOUTUBE_SCRIPT_URL = 'https://www.youtube.com/player_api';

const player = ref(null);
const playerId = `player-${props.videoId}-${Date.now()}`;
const isPlayerSet = ref(false);
const playButton = ref(null);

const showPlaceholder = ref(true);

function replaceVideoId() {
  const video = document.querySelector(`#${playerId}`);
  if (video) {
    video.title = `YouTube video: ${video.title}`;
  }
}

function setupPlayer() {
  setupPlayerAndBindToEvents()
    .then(() => {
      tryPlayVideo(player.value);
    })
    .then(() => {
      playVideo();
      replaceVideoId();
      togglePlaceholder();
    });
}

function setupPlayerAndBindToEvents() {
  return new Promise((resolve) => {
    setupYouTube().then(() => {
      // eslint-disable-next-line no-undef
      player.value = new YT.Player(playerId, {
        videoId: props.videoId,
        host: props.host,

        playerVars: {
          // if the `rel` parameter is set to `0`,
          // related videos will come from the same channel
          // as the video that was just played.
          // rel is no longer for disabling related videos.
          rel: 0,
          mute: 1,
          modestbranding: 1,
        },

        events: {
          onReady: () => {
            resolve();
          },
        },
      });
    });
  });
}

function setupYouTube(delay = 100, tries = 5) {
  return new Promise((resolve, reject) => {
    let interval = null;
    let tryCount = 0;

    interval = setInterval(() => {
      // eslint-disable-next-line no-undef
      if (typeof YT !== 'undefined' && YT.loaded === 1 && typeof YT.Player !== 'undefined') {
        clearInterval(interval);
        isPlayerSet.value = true;
        // eslint-disable-next-line no-undef
        resolve(YT);
      } else if (tryCount >= tries) {
        clearInterval(interval);
        reject(new TypeError('YouTube player setup has failed!'));
      }

      ++tryCount;
    }, delay);
  });
}

function tryPlayVideo(player) {
  return new Promise((resolve, reject) => {
    try {
      player.playVideo();
      resolve();
    } catch (e) {
      reject(new TypeError('YouTube player playback has failed!', e));
    }
  });
}

function play() {
  if (!isPlayerSet.value) {
    loadYouTubeScript.value = true;
  } else {
    togglePlaceholder();
    playVideo();
  }
}

function stop() {
  togglePlaceholder();
  stopVideo();

  // to fulfill a11y requirement
  setTimeout(() => {
    playButton.value.focus();
  }, 250);
}

function togglePlaceholder() {
  showPlaceholder.value = !showPlaceholder.value;
}

function playVideo() {
  if (player.value && player.value.playVideo) {
    player.value.playVideo();

    // to fulfill a11y requirement
    const iframe = document.querySelector(`#${playerId}`);
    if (iframe) {
      iframe.contentWindow.focus();
    }
  }
}

function stopVideo() {
  if (player.value && player.value.stopVideo) {
    player.value.stopVideo();
  }
}
</script>

<style lang="scss">
@import '../../styles/style.scss';
@import './TceVideoYoutube.scss';
</style>
